import React, { useState } from 'react';
import { 
  FaBell, 
  FaUserCircle, 
  FaChevronDown,
  FaSignOutAlt,
  FaCog,
  FaUser,
  FaSearch,
  FaVolumeUp
} from 'react-icons/fa';
import SoundSettings from './SoundSettings';
import { useToastWithSound } from '../hooks/useToastWithSound';

const ModernHeader = ({ user, onLogout, isStaffPanel = false }) => {
  const [showProfileMenu, setShowProfileMenu] = useState(false);
  const [showNotifications, setShowNotifications] = useState(false);
  const [showSoundSettings, setShowSoundSettings] = useState(false);
  const { showActionToast } = useToastWithSound();

  const notifications = [
    { id: 1, message: 'New service request received', time: '2 min ago', unread: true },
    { id: 2, message: 'Monthly report is ready', time: '1 hour ago', unread: true },
    { id: 3, message: 'Staff member updated profile', time: '3 hours ago', unread: false }
  ];

  const unreadCount = notifications.filter(n => n.unread).length;

  return (
    <header className={`bg-white shadow-lg border-b border-gray-200 sticky top-0 z-40 ${!isStaffPanel ? 'ml-64' : ''}`}>
      <div className="px-6 py-4">
        <div className="flex items-center justify-between">
          {/* Search Bar */}
          <div className="flex-1 max-w-md">
            <div className="relative">
              <FaSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search users, services, reports..."
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
          </div>

          {/* Right Section */}
          <div className="flex items-center space-x-4">
            {/* Sound Settings - Admin Only */}
            {user?.role === 'Admin' && (
              <button
                onClick={() => setShowSoundSettings(true)}
                className="p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
                title="Sound Settings (Admin Only)"
              >
                <FaVolumeUp className="text-xl" />
              </button>
            )}

            {/* Notifications */}
            <div className="relative">
              <button
                onClick={() => setShowNotifications(!showNotifications)}
                className="relative p-2 text-gray-600 hover:text-gray-800 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <FaBell className="text-xl" />
                {unreadCount > 0 && (
                  <span className="absolute -top-1 -right-1 bg-red-500 text-white text-xs rounded-full h-5 w-5 flex items-center justify-center">
                    {unreadCount}
                  </span>
                )}
              </button>

              {/* Notifications Dropdown */}
              {showNotifications && (
                <div className="absolute right-0 mt-2 w-80 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <h3 className="font-semibold text-gray-800">Notifications</h3>
                  </div>
                  <div className="max-h-64 overflow-y-auto">
                    {notifications.map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-4 border-b border-gray-100 hover:bg-gray-50 ${
                          notification.unread ? 'bg-blue-50' : ''
                        }`}
                      >
                        <p className="text-sm text-gray-800">{notification.message}</p>
                        <p className="text-xs text-gray-500 mt-1">{notification.time}</p>
                      </div>
                    ))}
                  </div>
                  <div className="p-3 text-center border-t border-gray-200">
                    <button className="text-blue-600 hover:text-blue-800 text-sm font-medium">
                      View All Notifications
                    </button>
                  </div>
                </div>
              )}
            </div>

            {/* Profile Menu */}
            <div className="relative">
              <button
                onClick={() => setShowProfileMenu(!showProfileMenu)}
                className="flex items-center space-x-3 p-2 text-gray-700 hover:bg-gray-100 rounded-lg transition-colors"
              >
                <div className="flex items-center space-x-2">
                  <div className="w-8 h-8 bg-blue-600 rounded-full flex items-center justify-center">
                    <FaUserCircle className="text-white" />
                  </div>
                  <div className="text-left hidden md:block">
                    <p className="text-sm font-medium">{user?.name || 'User'}</p>
                    <p className="text-xs text-gray-500">{user?.role || 'Staff'}</p>
                  </div>
                </div>
                <FaChevronDown className="text-xs text-gray-400" />
              </button>

              {/* Profile Dropdown */}
              {showProfileMenu && (
                <div className="absolute right-0 mt-2 w-48 bg-white rounded-lg shadow-xl border border-gray-200 z-50">
                  <div className="p-4 border-b border-gray-200">
                    <p className="font-medium text-gray-800">{user?.name || 'User'}</p>
                    <p className="text-sm text-gray-500">{user?.email || 'No email'}</p>
                  </div>
                  
                  <div className="py-2">
                    <button 
                      onClick={() => {
                        setShowProfileMenu(false);
                        showActionToast('info', true, 'Profile Settings - Coming Soon! 👤');
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <FaUser className="text-sm" />
                      <span>Profile Settings</span>
                    </button>
                    <button 
                      onClick={() => {
                        setShowProfileMenu(false);
                        showActionToast('info', true, 'System Settings - Coming Soon! ⚙️');
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-left text-gray-700 hover:bg-gray-100 transition-colors"
                    >
                      <FaCog className="text-sm" />
                      <span>System Settings</span>
                    </button>
                  </div>
                  
                  <div className="border-t border-gray-200 py-2">
                    <button
                      onClick={() => {
                        showActionToast('logout', true);
                        onLogout();
                      }}
                      className="w-full flex items-center space-x-2 px-4 py-2 text-left text-red-600 hover:bg-red-50 transition-colors"
                    >
                      <FaSignOutAlt className="text-sm" />
                      <span>Sign Out</span>
                    </button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>
      
      {/* Sound Settings Modal */}
      <SoundSettings 
        isOpen={showSoundSettings} 
        onClose={() => setShowSoundSettings(false)} 
      />
    </header>
  );
};

export default ModernHeader;
